import React from "react";
import glamorous from "glamorous";

import Section from "../components/Section";
import * as Icons from "../components/FeatureIcons";
import FeatureButton from "../components/FeatureButton";
import { Hero, HeroTitle } from "../components/Hero";
import { Funnel, FunnelTitle } from "../components/Funnel";

import MantaTailImage from "../assets/manta-tail.png";
import FeaturesBG from "../assets/features-bg.png";
import FeatureSchematic from "../assets/features-schematic.png";

const HeroImage = glamorous.div(({ theme }) => ({
  background: `url("${FeatureSchematic}") no-repeat center`,
  backgroundSize: "contain",
  height: 300,
  marginRight: "4rem",
  maxWidth: 225,
  width: "30%",
  [theme.main.mediaQueries.mobile]: {
    display: "none"
  }
}));

const HeroText = glamorous.div(({ theme }) => ({
  textAlign: "left",
  width: "50%",
  [theme.main.mediaQueries.tablet]: {
    textAlign: "left",
    width: "60%",
  },
  [theme.main.mediaQueries.mobile]: {
    textAlign: "center",
    width: "75%"
  }
}));

const SectionContainer = glamorous.div(({ featurePosition = "left", theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: featurePosition === "bottom" ? "column" : "row",
  maxWidth: theme.main.maxWidths.default,
  [theme.main.mediaQueries.mobile]: {
    flexDirection: featurePosition === "left"
      ? "column-reverse"
      : "column"
  }
}));

const SectionDescription = glamorous.div(({ theme, glam = { position: "right" }}) => ({
  paddingLeft: glam.position === "right" ? "2rem" : 0,
  paddingRight: glam.position === "left" ? "2rem" : 0,
  width: "45%",
  [theme.main.mediaQueries.mobile]: {
    paddingLeft: 0,
    paddingRight: 0,
    textAlign: "center",
    width: "95%"
  }
}));
const SectionFeatures = glamorous.div(({ layout, theme }) => ({
  display: "flex",
  flexDirection: layout || "column",
  width: layout === "row" ? "100%" : "55%",
  [theme.main.mediaQueries.mobile]: {
    flexDirection: "column",
    width: "100%"
  }
}));

const Feature = glamorous.div(({ theme, layout = "horizontal", upcoming = false}) => ({
    alignItems: "center",
    border: "1px solid #eee",
    background: "#fff",
    borderRadius: 10,
    boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: layout === "horizontal" ? "row" : "column",
    flex: "0.5 1 45%",
    margin: "1rem",
    opacity: upcoming ? 0.5 : 1,
    padding: "1rem",
    transition: "0.25s",
    ":hover": upcoming
      ? {}
      : {
        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
        transform: "scale(1.02, 1.02)",
        transition: "0.25s"
      },
    [theme.main.mediaQueries.mobile]: {
      flexDirection: "row",
      padding: "1rem 0.75rem"
    },
    "> svg": {
      marginRight: layout === "horizontal" ? "5%" : 0,
      width: layout === "horizontal" ? "10%" : "100%",
      [theme.main.mediaQueries.mobile]: {
        marginRight: "5%",
        width: "15%"
      },
    },
    "> div": {
      width: "85%"
    }
  }));

const FeatureHeader = glamorous.h4({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "0.25rem",
  "> small": {
    color: "#ccc",
    fontSize: "0.75rem",
    textTransform: "uppercase"
  }
});

const FeatureDescription = glamorous.small({
  marginBottom: 0
});

const NarrowContainer = glamorous.div(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  maxWidth: theme.main.maxWidths.default
}));

const FeaturesPage = ({ onToggleContactForm }) => (
  <div>
    <Hero glam={{ background: `#BAE8CB url("${FeaturesBG}") repeat` }}>
      <NarrowContainer>
        <HeroImage />
        <HeroText>
          <HeroTitle>Simple, but flexible</HeroTitle>
          <glamorous.P fontSize="24px">
            All the features you need, when you need them. Track cameras, multi-functional rooms, self-service printers and just about anything else.
          </glamorous.P>
        </HeroText>
      </NarrowContainer>
    </Hero>
    <Section>
      <SectionContainer featurePosition="left">
        <SectionFeatures>
          <Feature>
            <Icons.Omnibox />
            <div>
              <FeatureHeader>Omnibox</FeatureHeader>
              <FeatureDescription>Find users, resources or bookings all in the same, intelligent search box.</FeatureDescription>
            </div>
          </Feature>
          <Feature>
            <Icons.Timeline />
            <div>
              <FeatureHeader>Timeline</FeatureHeader>
              <FeatureDescription>Understand your users&apos; booking history and fit bookings in a tight schedule.</FeatureDescription>
            </div>
          </Feature>
          <Feature>
            <Icons.UserPortal />
            <div>
              <FeatureHeader>
                <span>User portal</span>
                {/* <small>Beta</small> */}
              </FeatureHeader>
              <glamorous.P color="#ccc" fontWeight={100} marginBottom={0}>(Patron-initiated reservations)</glamorous.P>
              <FeatureDescription>Let patrons make reservations without stopping by the counter. Set constraints to prevent greedy bookings and save time on both ends.</FeatureDescription>
            </div>
          </Feature>
        </SectionFeatures>
        <SectionDescription glam={{ position: "right" }}>
          <h2>Streamlined workflow</h2>
          <p>Manta focuses on a light user interface that is intuitive to use and requires no training. Less clutter, faster bookings.</p>
        </SectionDescription>
      </SectionContainer>
    </Section>
    <Section>
      <SectionContainer featurePosition="right">
        <SectionDescription glam={{ position: "left" }}>
          <h2>Flexible features</h2>
          <p>We know each department works differently. Forget managing three calendars, spreadsheets and pen-and-paper logs. Configure Manta to work the way you need it.</p>
        </SectionDescription>
        <SectionFeatures>
          <Feature>
            <Icons.AutoCirculation />
            <div>
              <FeatureHeader>Auto-circulation</FeatureHeader>
              <FeatureDescription>Create items that automatically check out and return. Fantastic for printers and other equipment that works by appointment.</FeatureDescription>
            </div>
          </Feature>
          <Feature>
            <Icons.ServiceHours />
            <div>
              <FeatureHeader>Service Hours</FeatureHeader>
              <FeatureDescription>Full control over your schedule, including holidays and seasonal changes. Set default checkout times for different resources.</FeatureDescription>
            </div>
          </Feature>
          <Feature>
            <Icons.MetaFields />
            <div>
              <FeatureHeader>Meta fields</FeatureHeader>
              <FeatureDescription>Store and keep track of information that you care about. Add fields detailing service history, equipment features, or anything else.</FeatureDescription>
            </div>
          </Feature>
        </SectionFeatures>
      </SectionContainer>
    </Section>
    <Section css={{ background: "#a9dce3" }}>
      <SectionContainer featurePosition="bottom">
        <SectionDescription css={{ width: "100%", padding: 0, maxWidth: 750, textAlign: "center" }}>
          <h2>Seamless integration</h2>
          <p>Minimize the troubles of user and server management. Have us keep tabs on uptime and chat to a support team that responds in minutes.</p>
        </SectionDescription>
        <SectionFeatures layout="row">
          <Feature layout="vertical">
            <Icons.Cloud />
            <div>
              <FeatureHeader>On the Cloud</FeatureHeader>
              <FeatureDescription>No servers, passwords or infrastructure to manage. Manta takes minutes to set up, and we&apos;ll even take care of importing your resources and users.</FeatureDescription>
            </div>
          </Feature>
          <Feature layout="vertical">
            <Icons.SingleSignOn />
            <div>
              <FeatureHeader>
                <span>Single-sign on</span>
              </FeatureHeader>
              <FeatureDescription>Users should not have to create tens of accounts. Use the same sign on service your institution provides.</FeatureDescription>
            </div>
          </Feature>
          <Feature layout="vertical" upcoming={true}>
            <Icons.ActiveDirectory />
            <div>
              <FeatureHeader css={{ flexDirection: "column", alignItems: "left" }}>
                <span>Active Directory</span>
                <glamorous.Small paddingTop="0.5rem">Upcoming</glamorous.Small>
              </FeatureHeader>
              <FeatureDescription>Already have groups and classes created on Active Directory? Link them in directly with Manta and restrict who can book resouces that require training on enrollment.</FeatureDescription>
            </div>
          </Feature>
        </SectionFeatures>
      </SectionContainer>
    </Section>
    <Section className="integration">
      <SectionContainer featurePosition="bottom">
        <SectionDescription glam={{ position: "left" }}>
          <h1>Small and nimble</h1>
          <glamorous.P maxWidth={750} margin="0 auto 1.5rem">At Manta Equipment, we value working closely with our clients, iterating fast and building a product tailored to your needs.</glamorous.P>
          <p>Send us a message, and let us know what features you would like to see in Manta.</p>
          <glamorous.Img src={MantaTailImage} />
        </SectionDescription>
        {/*<SectionFeatures>
          <ContactForm>
            <glamorous.Div display="flex" justifyContent="space-between">
              <input placeholder="Full name" type="text" name="name" />
              <input placeholder="Email address" type="email" name="email" />
              <input placeholder="Institution / company" type="text" name="organization" />
            </glamorous.Div>
            <textarea placeholder="Your message..." rows={7} />
            <glamorous.Div display="flex" alignItems="center" justifyContent="space-between">
              <ContactFormSubmitButton type="submit" value="Send" />
              <div className="g-recaptcha" data-sitekey="6LfvsEUUAAAAAFqHBCFYslkQHKP6NhHUO7waeg5D"></div>
            </glamorous.Div>
          </ContactForm>
        </SectionFeatures>*/}
      </SectionContainer>
    </Section>
    <Funnel>
      <FunnelTitle css={{ paddingRight: "3rem" }}>Try Manta Equipment now</FunnelTitle>
      <FeatureButton onClick={onToggleContactForm}>Arrange a demo</FeatureButton>
    </Funnel>
  </div>
);

export default FeaturesPage;



// WEBPACK FOOTER //
// ./src/pages/features.js